import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShare } from '../hooks/useShare';
import { useReferralCommission } from '../hooks/useReferralCommission';
import { useReferredUsers } from '../hooks/useReferredUsers';
import { formatGhanaCedis } from '../utils/formatUtils';
import iconLogo from '../assets/icon.png';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  GiftIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  BookOpenIcon,
  UsersIcon,
  TrophyIcon,
  ShareIcon,
  CheckIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';

const AffiliatePage: React.FC = () => {
  const { currentUser, updateProfile } = useAuth();
  const { copyToClipboard: shareCopyToClipboard } = useShare();
  const navigate = useNavigate();
  const location = useLocation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState('');
  
  // Use referral commission hook
  const { 
    summary: commissionSummary, 
    transactions: commissionTransactions, 
    loading: commissionLoading 
  } = useReferralCommission();

  // Use referred users hook
  const { 
    referredUsers, 
    loading: referredUsersLoading, 
    totalReferred 
  } = useReferredUsers();

  // Check if there's a referral code in URL params
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
    }
  }, [location]);

  const copyToClipboard = async (text: string, field: string) => {
    const success = await shareCopyToClipboard(text, `${field} copied to clipboard!`);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const generateReferralLink = (code: string) => {
    return `${window.location.origin}/signup?ref=${code}`;
  };

  // Generate a new referral code for a given user id (same scheme as auth)
  const generateNewReferralCode = (uid: string) => {
    const timestamp = Date.now().toString(36);
    const uidPart = uid.substring(0, 6);
    return `${uidPart}${timestamp}`.toUpperCase();
  };

  const handleRegenerateReferralCode = async () => {
    if (!currentUser) return;
    const confirmed = window.confirm(
      'Regenerating your affiliate code will invalidate the old one. Continue?'
    );
    if (!confirmed) return;
    try {
      const newCode = generateNewReferralCode(currentUser.uid);
      await updateProfile({ referralCode: newCode });
      toast.success('Affiliate code regenerated');
    } catch (err) {
      toast.error('Failed to regenerate code');
    }
  };

  const handleGuestSignup = () => {
    const signupUrl = referralCode ? `/signup?ref=${referralCode}` : '/signup';
    navigate(signupUrl);
  };

  const affiliateBenefits = [
    {
      icon: CurrencyDollarIcon,
      title: "Earn Money",
      description: "Get paid for every successful referral",
      color: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      icon: GiftIcon,
      title: "Exclusive Rewards",
      description: "Access to special bonuses and incentives",
      color: "text-purple-600",
      bgColor: "bg-purple-100"
    },
    {
      icon: ChartBarIcon,
      title: "Track Performance",
      description: "Monitor your referrals and earnings",
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Sign Up",
      description: "Create your account and get your unique referral code",
      icon: UserGroupIcon
    },
    {
      step: 2,
      title: "Share Your Code",
      description: "Share your referral code or link with friends and family",
      icon: ShareIcon
    },
    {
      step: 3,
      title: "Earn Rewards",
      description: "Get paid when your referrals sign up and enroll in courses",
      icon: TrophyIcon
    }
  ];

  const referralStats = [
    { label: "Active Affiliates", value: "500+", icon: UsersIcon },
    { label: "Total Referrals", value: "2,500+", icon: UserGroupIcon },
    { label: "Paid Out", value: formatGhanaCedis(50000), icon: CurrencyDollarIcon },
    { label: "Success Rate", value: "85%", icon: ChartBarIcon }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Hero Section (matching course page style) */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4">
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <UserGroupIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Affiliate Program
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Earn By Referring Friends</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Share. Invite. Earn.
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Share your unique link or code. Get rewarded when your referrals join and learn.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start">
                <a href="#affiliate-dashboard" className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm">
                  View Dashboard
                </a>
                {!currentUser && (
                  <button onClick={() => navigate('/signup' + (referralCode ? `?ref=${referralCode}` : ''))} className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm">
                    Get Started
                  </button>
                )}
              </div>
            </div>

            {/* Right - Visuals (desktop) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <ShareIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Easy Sharing</h3>
                      <p className="text-blue-200 text-sm">Copy and share anywhere</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <CurrencyDollarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Real Rewards</h3>
                      <p className="text-blue-200 text-sm">Earn commissions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <main id="affiliate-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Referral Dashboard for Logged-in Users */}
        {currentUser && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Your Referral Dashboard</h2>
              <div className="text-sm text-gray-500">
                Member since {currentUser.createdAt ? new Date(currentUser.createdAt).toLocaleDateString() : 'N/A'}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <UserGroupIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="text-2xl font-bold text-primary-700">{currentUser.totalrefs || 0}</div>
                <div className="text-sm text-primary-600">Total Referrals</div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-700">{formatGhanaCedis(currentUser.earnings || 0)}</div>
                <div className="text-sm text-green-600">Total Earnings</div>
                {currentUser.earnings > 0 && (
                  <Link
                    to="/withdraw"
                    className="inline-block mt-3 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Withdraw Now
                  </Link>
                )}
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ChartBarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text-2xl font-bold text-purple-700">
                  {currentUser.referralCode ? 'Active' : 'Inactive'}
                </div>
                <div className="text-sm text-purple-600">Referral Status</div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <StarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {currentUser.referredBy ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-blue-600">Referred By</div>
              </div>
            </div>

            {/* Referral Code Section */}
            {currentUser.referralCode ? (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Code</h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                  <div className="flex-1 bg-white border border-gray-300 rounded-md px-4 py-3 font-mono text-base sm:text-lg break-all">
                    {currentUser.referralCode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(currentUser.referralCode!, "Referral Code")}
                    className={`w-full sm:w-auto px-4 py-3 rounded-md transition-colors ${
                      copiedField === "Referral Code"
                        ? "bg-green-100 text-green-700"
                        : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                    }`}
                  >
                    {copiedField === "Referral Code" ? (
                      <CheckIcon className="h-5 w-5" />
                    ) : (
                      <ClipboardDocumentIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={handleRegenerateReferralCode}
                    className="w-full sm:w-auto px-4 py-3 rounded-md transition-colors bg-red-100 text-red-700 hover:bg-red-200"
                    title="Regenerate code"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Share this code with friends and family to start earning!
                </p>
              </div>
            ) : (
              <div className="bg-yellow-50 rounded-lg p-6 text-center">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Referral Code Yet</h3>
                <p className="text-yellow-700 mb-4">
                  You don't have a referral code yet. Generate one now to start sharing.
                </p>
                <button
                  onClick={handleRegenerateReferralCode}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  Generate Referral Code
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </button>
              </div>
            )}

            {/* Referral Link Section */}
            {currentUser.referralCode && (
              <div className="bg-gray-50 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Complete Affiliate Link</h3>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <input
                      type="text"
                      value={`${window.location.origin}/affiliate?ref=${currentUser.referralCode}`}
                      readOnly
                      className="flex-1 bg-white border border-gray-300 rounded-md px-4 py-3 font-mono text-sm truncate"
                    />
                    <button
                      onClick={() => copyToClipboard(`${window.location.origin}/affiliate?ref=${currentUser.referralCode}`, "Affiliate Link")}
                      className={`w-full sm:w-auto px-4 py-3 rounded-md transition-colors ${
                        copiedField === "Affiliate Link"
                          ? "bg-green-100 text-green-700"
                          : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                      }`}
                    >
                      {copiedField === "Affiliate Link" ? (
                        <CheckIcon className="h-5 w-5" />
                      ) : (
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Share this complete link with potential referrals. When they click it, your referral code will be automatically applied.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Benefits Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {affiliateBenefits.map((benefit, index) => (
            <div key={index} className="bg-white p-8 rounded-lg shadow-md">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${benefit.bgColor}`}>
                <benefit.icon className={`w-6 h-6 ${benefit.color}`} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-700">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* How It Works Section */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mr-4 text-primary-600">
                  <step.icon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-700">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {referralStats.map((stat, index) => (
            <div key={index} className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4 text-primary-600">
                <stat.icon className="w-6 h-6" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.label}</p>
              <p className="text-gray-700">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Referral Program Overview */}
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Referral Program Overview</h2>
          <p className="text-gray-700 mb-6">
            ProfStudy offers a comprehensive referral program that rewards both new and existing users.
            When you refer a friend or family member, they can use your unique referral code to sign up.
            For every successful referral, you earn a commission.
          </p>
          <p className="text-gray-700 mb-6">
            The referral program is designed to be transparent and rewarding.
            You can track your earnings, referrals, and success rate in your dashboard.
          </p>
          <p className="text-gray-700">
            Ready to start earning? Click "Get Started" to create your account and get your unique referral code.
            Share it with your network to start referring!
          </p>
        </div>

        {/* Commission Structure */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Commission Structure</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">How Much You Earn</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <p className="font-medium text-green-800">Course Enrollment</p>
                    <p className="text-sm text-green-600">When your referral enrolls in a course</p>
                  </div>
                  <span className="text-2xl font-bold text-green-700">15%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div>
                    <p className="font-medium text-blue-800">Book Purchase</p>
                    <p className="text-sm text-blue-600">When your referral buys study materials</p>
                  </div>
                  <span className="text-2xl font-bold text-blue-700">10%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <p className="font-medium text-purple-800">Premium Subscriptions</p>
                    <p className="text-sm text-purple-600">Monthly/annual premium memberships</p>
                  </div>
                  <span className="text-2xl font-bold text-purple-700">20%</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Earnings Summary</h3>
              {commissionLoading ? (
                <div className="space-y-4">
                  <div className="animate-pulse">
                    <div className="h-16 bg-gray-200 rounded-lg"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-16 bg-gray-200 rounded-lg"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-16 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-900 mb-2">Total Earnings</h4>
                    <p className="text-2xl font-bold text-green-700">
                      {formatGhanaCedis(commissionSummary.totalEarnings)}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">Pending Commissions</h4>
                    <p className="text-lg font-semibold text-blue-700">
                      {formatGhanaCedis(commissionSummary.pendingCommissions)}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2">Total Referrals</h4>
                    <p className="text-lg font-semibold text-purple-700">
                      {commissionSummary.totalReferrals}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Referral Performance Dashboard */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Referral Performance</h2>
          
          {/* Referral Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
              <div className="flex items-center">
                <UsersIcon className="h-8 w-8 mr-3" />
                <div>
                  <p className="text-sm opacity-90">Total People Referred</p>
                  <p className="text-3xl font-bold">{commissionSummary.totalReferrals}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-8 w-8 mr-3" />
                <div>
                  <p className="text-sm opacity-90">Total Earnings</p>
                  <p className="text-3xl font-bold">{formatGhanaCedis(commissionSummary.totalEarnings)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-lg">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 mr-3" />
                <div>
                  <p className="text-sm opacity-90">Pending Commissions</p>
                  <p className="text-3xl font-bold">{formatGhanaCedis(commissionSummary.pendingCommissions)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg">
              <div className="flex items-center">
                <ChartBarIcon className="h-8 w-8 mr-3" />
                <div>
                  <p className="text-sm opacity-90">Conversion Rate</p>
                  <p className="text-3xl font-bold">
                    {commissionSummary.totalReferrals > 0 
                      ? Math.round((commissionSummary.totalReferrals / (commissionSummary.totalReferrals + 10)) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Code Sharing */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Referral Code</h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
              <div className="flex-1 bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Share this code with friends:</p>
                <p className="text-xl sm:text-2xl font-mono font-bold text-primary-600 break-all">{currentUser?.referralCode || 'N/A'}</p>
              </div>
              <button
                onClick={() => copyToClipboard(currentUser?.referralCode || '', 'referral code')}
                className="w-full sm:w-auto px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {copiedField === 'referral code' ? 'Copied!' : 'Copy Code'}
              </button>
            </div>
            
            {/* Referral Link */}
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Or share your referral link:</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
                <div className="flex-1 bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm font-mono text-gray-800 break-all">
                    {window.location.origin}/signup?ref={currentUser?.referralCode}
                  </p>
                </div>
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/signup?ref=${currentUser?.referralCode}`, 'referral link')}
                  className="w-full sm:w-auto px-6 py-3 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
                >
                  {copiedField === 'referral link' ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Transactions */}
        {commissionTransactions.length > 0 && (
          <div className="bg-white p-8 rounded-lg shadow-md mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Recent Commission Transactions</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionTransactions.slice(0, 10).map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.orderId}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="space-y-1">
                          {transaction.items.map((item, index) => (
                            <div key={index} className="text-xs">
                              {item.name} ({item.type})
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatGhanaCedis(transaction.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatGhanaCedis(transaction.commissionAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.status === 'approved' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Program Rules & Guidelines */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Rules & Guidelines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What's Allowed</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Share your referral code on social media</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Send referral links via email or messaging</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Include referral codes in blog posts or content</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Refer friends, family, and colleagues</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What's Not Allowed</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Spam or unsolicited messages</span>
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Fake accounts or self-referrals</span>
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Misleading claims about earnings</span>
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Violation of platform terms of service</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Success Tips */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tips for Success</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Build Your Network</h3>
              <p className="text-gray-700">Connect with people in your professional field and share valuable content</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShareIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Share Strategically</h3>
              <p className="text-gray-700">Share your referral code when it's relevant and adds value</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <StarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Provide Value</h3>
              <p className="text-gray-700">Help your referrals succeed by sharing your learning experience</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I get paid?</h3>
              <p className="text-gray-700">Earnings are automatically credited to your account. You can withdraw when you reach GH₵50 minimum balance using mobile money or bank transfer.</p>
            </div>
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">When do I receive my commission?</h3>
              <p className="text-gray-700">Commissions are credited immediately after your referral completes their first purchase or enrollment.</p>
            </div>
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I refer myself?</h3>
              <p className="text-gray-700">No, self-referrals are not allowed and will result in account suspension.</p>
            </div>
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is there a limit to referrals?</h3>
              <p className="text-gray-700">No limit! You can refer as many people as you want and earn commissions from all successful referrals.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if my referral doesn't complete their purchase?</h3>
              <p className="text-gray-700">You only earn commissions when referrals complete their purchases. Incomplete transactions don't qualify for commissions.</p>
            </div>
          </div>
        </div>

        {/* Call to Action for Guest Users */}
        {!currentUser && (
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-8 text-center text-white mt-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Earning?</h2>
            <p className="text-xl mb-6 opacity-90">
              Join thousands of affiliates who are already earning money by referring friends and family.
              {referralCode && (
                <span className="block mt-2 text-lg">
                  You were referred by someone! Use their code: <strong>{referralCode}</strong>
                </span>
              )}
            </p>
            {/* Enter someone's affiliate code */}
            <div className="max-w-md mx-auto w-full mb-6">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.trim())}
                placeholder="Enter a friend's affiliate code (optional)"
                className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGuestSignup}
                className="w-full sm:w-auto bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                Get Started Now
              </button>
              <Link
                to="/signin"
                className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                I Already Have an Account
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">ProfStudy</h3>
              <p className="text-gray-700">
                Your one-stop platform for online learning.
                We offer a wide range of courses to help you achieve your goals.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h3>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-700 hover:text-gray-900">About Us</Link></li>
                <li><Link to="/courses" className="text-gray-700 hover:text-gray-900">Courses</Link></li>
                <li><Link to="/contact" className="text-gray-700 hover:text-gray-900">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Legal</h3>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-gray-700 hover:text-gray-900">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-700 hover:text-gray-900">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-700 hover:text-gray-900">
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796V24C19.612 23.046 24 18.082 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-700 hover:text-gray-900">
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.223-.617v.06a4.923 4.923 0 003.946 4.827 9.86 9.86 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.007-.63A9.935 9.935 0 0024 4.57z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-700 hover:text-gray-900">
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796V24C19.612 23.046 24 18.082 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center text-gray-700 text-sm">
            &copy; {new Date().getFullYear()} ProfStudy. All rights reserved.
          </div>
        </div>
      </footer>

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default AffiliatePage;
